import { refreshAccessToken } from '@/mocked_api/auth';
import { router } from '@/router';

import { clearTokens, getTokens, isAccessTokenExpired, isRefreshTokenExpired, saveTokens } from './auth-token';

// Module-level state — shared across all concurrent requests.
// This ensures only one refresh call fires even when multiple requests get a 401 simultaneously.
let isRefreshing = false;

let refreshPromise: Promise<void> | null = null;

export async function ensureFreshToken(): Promise<string | null> {
	const tokens = getTokens();

	if (!tokens) return null;

	if (isRefreshTokenExpired(tokens)) {
		clearTokens();
		void router.navigate({ to: '/sign-in' });

		return null;
	}

	if (!isAccessTokenExpired(tokens)) {
		return tokens.accessToken;
	}

	// Access token is expired — refresh once, all concurrent callers await the same promise.
	if (!isRefreshing) {
		isRefreshing = true;
		refreshPromise = refreshAccessToken(tokens.refreshToken)
			.then((refreshed) => {
				saveTokens({
					...tokens,
					accessToken: refreshed.accessToken,
					accessTokenExpiration: refreshed.accessTokenExpiration,
				});
			})
			.catch(() => {
				clearTokens();
				void router.navigate({ to: '/sign-in' });
				throw new Error('Session expired');
			})
			.finally(() => {
				isRefreshing = false;
				refreshPromise = null;
			});
	}

	await refreshPromise;

	return getTokens()?.accessToken ?? null;
}

export function resetRefreshState(): void {
	isRefreshing = false;
	refreshPromise = null;
}
